import { productIdentity } from "@papersflow/contracts";

export const APPEARANCE_STORAGE_KEY = "papersflow-appearance";
export const ACCENT_STORAGE_KEY = "papersflow-accent";
export const GLASS_OPACITY_STORAGE_KEY = "papersflow-glass-opacity";
export const FILE_PREVIEW_WORD_WRAP_STORAGE_KEY = "papersflow-file-preview-word-wrap";

export const appearanceModes = ["system", "light", "dark"] as const;
export type AppearanceMode = (typeof appearanceModes)[number];

export const accentPresets = [
  { value: "papersflow", label: productIdentity.displayName },
  { value: "rose", label: "Rose" },
  { value: "grove", label: "Grove" },
  { value: "ocean", label: "Ocean" },
  { value: "ember", label: "Ember" },
  { value: "iris", label: "Iris" },
] as const;
export type AccentPreset = (typeof accentPresets)[number]["value"];

export const GLASS_OPACITY_MIN = 45;
export const GLASS_OPACITY_MAX = 100;
export const DEFAULT_GLASS_OPACITY = 80;

export interface AppearancePreferences {
  readonly mode: AppearanceMode;
  readonly accent: AccentPreset;
  readonly glassOpacity: number;
  readonly filePreviewWordWrap: boolean;
}

let systemColorQuery: MediaQueryList | undefined;
let systemColorListener: ((event: MediaQueryListEvent) => void) | undefined;
let filePreviewWordWrapRuntimePreference: boolean | undefined;

function storedValue(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function persistValue(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    // The visual preference still applies for the current page when storage is unavailable.
  }
}

function isAppearanceMode(value: string | null): value is AppearanceMode {
  return appearanceModes.some((mode) => mode === value);
}

function isAccentPreset(value: string | null): value is AccentPreset {
  return accentPresets.some((preset) => preset.value === value);
}

function readGlassOpacity(value: string | null): number {
  const parsed = value === null ? Number.NaN : Number(value);
  return Number.isInteger(parsed) && parsed >= GLASS_OPACITY_MIN && parsed <= GLASS_OPACITY_MAX
    ? parsed
    : DEFAULT_GLASS_OPACITY;
}

export function readFilePreviewWordWrapPreference(): boolean {
  return (
    filePreviewWordWrapRuntimePreference ??
    storedValue(FILE_PREVIEW_WORD_WRAP_STORAGE_KEY) === "true"
  );
}

export function readAppearancePreferences(): AppearancePreferences {
  const storedMode = storedValue(APPEARANCE_STORAGE_KEY);
  const storedAccent = storedValue(ACCENT_STORAGE_KEY);
  return {
    mode: isAppearanceMode(storedMode) ? storedMode : "dark",
    accent: isAccentPreset(storedAccent) ? storedAccent : "papersflow",
    glassOpacity: readGlassOpacity(storedValue(GLASS_OPACITY_STORAGE_KEY)),
    filePreviewWordWrap: readFilePreviewWordWrapPreference(),
  };
}

function stopSystemColorListener() {
  if (systemColorQuery && systemColorListener) {
    systemColorQuery.removeEventListener("change", systemColorListener);
  }
  systemColorQuery = undefined;
  systemColorListener = undefined;
}

function applyResolvedSystemColor(dark: boolean) {
  document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
}

function applyAppearanceMode(mode: AppearanceMode) {
  stopSystemColorListener();
  document.documentElement.setAttribute("data-appearance", mode);
  if (mode !== "system") {
    document.documentElement.setAttribute("data-theme", mode);
    return;
  }

  systemColorQuery = window.matchMedia("(prefers-color-scheme: dark)");
  applyResolvedSystemColor(systemColorQuery.matches);
  systemColorListener = (event) => applyResolvedSystemColor(event.matches);
  systemColorQuery.addEventListener("change", systemColorListener);
}

function applyAccent(accent: AccentPreset) {
  document.documentElement.setAttribute("data-accent", accent);
}

function applyGlassOpacity(glassOpacity: number) {
  document.documentElement.style.setProperty("--glass-opacity", `${glassOpacity}%`);
}

export function initializeAppearancePreferences(): AppearancePreferences {
  const preferences = readAppearancePreferences();
  applyAppearanceMode(preferences.mode);
  applyAccent(preferences.accent);
  applyGlassOpacity(preferences.glassOpacity);
  return preferences;
}

export function setAppearanceMode(mode: AppearanceMode) {
  persistValue(APPEARANCE_STORAGE_KEY, mode);
  applyAppearanceMode(mode);
}

export function setAccentPreference(accent: AccentPreset) {
  persistValue(ACCENT_STORAGE_KEY, accent);
  applyAccent(accent);
}

export function setGlassOpacityPreference(glassOpacity: number) {
  const boundedOpacity = Math.min(
    GLASS_OPACITY_MAX,
    Math.max(GLASS_OPACITY_MIN, Math.round(glassOpacity)),
  );
  persistValue(GLASS_OPACITY_STORAGE_KEY, String(boundedOpacity));
  applyGlassOpacity(boundedOpacity);
}

export function setFilePreviewWordWrapPreference(wordWrap: boolean) {
  filePreviewWordWrapRuntimePreference = wordWrap;
  persistValue(FILE_PREVIEW_WORD_WRAP_STORAGE_KEY, String(wordWrap));
}
